// ---------------------------------------------------------------------------
// registry — centralized card registration (the dashboard extension seam)
// ---------------------------------------------------------------------------
//
// Adding a new card type = add one factory call here. The page core, layout
// controller, reorder, and persistence stay closed for modification. Role-gating
// (the events-translation card) is applied here, once.

import { createAlertCard } from './cards/alert-card.js';
import { createStatCard } from './cards/stat-card.js';
import { createChartCard } from './cards/chart-card.js';

/**
 * @typedef {import('../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 * @typedef {import('../../contracts/card-grid.js').CardHandle} CardHandle
 * @typedef {import('../../contracts/dashboard.js').DashboardCardContext} CardContext
 * @typedef {import('../../contracts/dashboard.js').StatsResponse} StatsResponse
 * @typedef {CardDescriptor & { render: (ctx: CardContext) => CardHandle }} DashboardCard
 */

/**
 * Build the ordered list of all card descriptors available to the current user.
 * Registration order here is the default layout order.
 *
 * @param {CardContext} ctx
 * @returns {DashboardCard[]}
 */
export function buildCardDescriptors(ctx) {
  const roles = ctx.auth?.user?.roles ?? [];
  const isAdmin = roles.includes('admin');

  /** @type {DashboardCard[]} */
  const cards = [
    // Overview spans the full top row on desktop.
    sized(createStatCard({ id: 'stats.totals', title: 'Overview', trendKey: 'visitors' }), 4, 1),

    // Alert lists are tall (2 rows); banners gets extra width for long titles.
    sized(createAlertCard({
      id: 'alerts.banners', title: 'Banners expiring', kind: 'banners',
      sliceKey: 'banners', listPath: '/banners',
    }), 2, 2),
    sized(createAlertCard({
      id: 'alerts.catalog', title: 'Catalog expiring', kind: 'catalog',
      sliceKey: 'catalog', listPath: '/company',
    }), 2, 2),
    sized(createAlertCard({
      id: 'alerts.advertorials', title: 'Advertorials expiring', kind: 'advertorials',
      sliceKey: 'advertorials', listPath: '/advertorial',
    }), 2, 2),

    // Charts: wide + tall so the SVG has room.
    sized(createChartCard({
      id: 'charts.byCountry', title: 'By country', chart: 'donut',
      valueHeader: 'Count',
      select: (s) => (Array.isArray(s.byCountry) ? s.byCountry : []).map((d) => ({
        label: String(d.country ?? ''), value: Number(d.count) || 0,
      })),
    }), 2, 2),
    sized(createChartCard({
      id: 'charts.byType', title: 'By type', chart: 'bar',
      valueHeader: 'Count',
      select: (s) => (Array.isArray(s.byType) ? s.byType : []).map((d) => ({
        label: String(d.type ?? ''), value: Number(d.count) || 0,
      })),
    }), 2, 2),
  ];

  // Role-gated card: untranslated events (legacy role <= 2 → admin).
  if (isAdmin) {
    cards.push(sized(createAlertCard({
      id: 'alerts.events', title: 'Events translation', kind: 'events',
      sliceKey: 'eventsTranslation', listPath: '/expo-translations',
    }), 4, 2));
  }

  return cards;
}

/**
 * Apply a default grid size to a descriptor (render-only; not persisted).
 * @param {DashboardCard} descriptor
 * @param {1|2|3|4} colSpan
 * @param {1|2} rowSpan
 * @returns {DashboardCard}
 */
function sized(descriptor, colSpan, rowSpan) {
  return { ...descriptor, colSpan, rowSpan };
}
